import React, { useCallback, useMemo, useState, useEffect } from 'react';
import classNames from 'classnames';
import update from 'immutability-helper';
import OutlinedDrag from 'acud-icon/lib/icons/OutlinedDrag';
import OutlinedEdit from 'acud-icon/lib/icons/OutlinedEdit';
import OutlinedDelete from 'acud-icon/lib/icons/OutlinedDelete';
import OutlinedPlus from 'acud-icon/lib/icons/OutlinedPlus';
import RcTree, { TreeNode } from 'rc-tree';
import Input from '../input';
import Popover from '../popover';
import { getParentKey, editNode, saveNode, deleteNode, changeNode, addNode } from './utils/nodeHelper';
import { renderSwitcherIcon } from './utils/iconHelper';
import { dropIndicatorRender } from './utils/dragHelper';
import { ConfigContext } from '../config-provider';
import PopoverComponent from './PopoverComponent';
var DataChangeType;
(function (DataChangeType) {
    DataChangeType["SAVE"] = "save";
    DataChangeType["EDITINPUTCHANGE"] = "editInputChange";
    DataChangeType["DELETE"] = "delete";
    DataChangeType["ADD"] = "add";
    DataChangeType["DROP"] = "drop";
})(DataChangeType || (DataChangeType = {}));
var UtilType;
(function (UtilType) {
    UtilType["Add"] = "Add";
    UtilType["Edit"] = "Edit";
    UtilType["Delete"] = "Delete";
})(UtilType || (UtilType = {}));
;
const generateList = (data) => {
    const dataList = [];
    const helper = (data) => {
        for (let i = 0; i < data.length; i++) {
            const node = data[i];
            const { key } = node;
            dataList.push({ key, title: key });
            if (node.children) {
                helper(node.children);
            }
        }
    };
    helper(data);
    return dataList;
};
const handleClickAndDbClick = (clickFunc, dbClickFunc, time) => {
    let count = 0;
    return (...args) => {
        count++;
        setTimeout(() => {
            if (count === 1) {
                clickFunc(...args);
            }
            else if (count === 2) {
                dbClickFunc(...args);
            }
            ;
            count = 0;
        }, time);
    };
};
const isSearchTitle = (title) => {
    return typeof title === 'string' || typeof title === 'number';
};
const isUtilType = (val) => typeof val === 'string' && val in UtilType;
const DEFAULT_POPOVER_UTIL = [
    {
        text: '新建节点',
        onClick: UtilType.Add
    },
    {
        text: '编辑节点',
        onClick: UtilType.Edit
    },
    {
        text: '删除节点',
        onClick: UtilType.Delete
    }
];
const Tree = React.forwardRef((props, ref) => {
    const { selectable = true, searchable, searchPlaceholder, checkable = false, editable, editInputProps, draggable, showIconUtil, showPopoverUtil, customPopoverUtil = DEFAULT_POPOVER_UTIL, prefixCls: customizePrefixCls, className, showLine, switcherIcon, onDataChange, treeData: treeDataOrigin, defaultExpandedKeys, expandedKeys: expandedKeysInProps, titleEllipsis, popoverZIndex } = props;
    const [expandedKeys, setExpandedKeys] = useState(expandedKeysInProps || defaultExpandedKeys || []);
    const [autoExpandParent, setAutoExpandParent] = useState(true);
    const [treeData, setTreeData] = useState([]);
    const [searchText, setSearchText] = useState();
    const [searchFound, setSearchFound] = useState(false);
    const treeRef = React.createRef();
    React.useImperativeHandle(ref, () => treeRef.current);
    const dataList = useMemo(() => generateList(treeData), [treeData]);
    const draggableConfig = useMemo(() => {
        if (!draggable) {
            return false;
        }
        return {
            icon: <OutlinedDrag />
        };
    }, [draggable]);
    const onExpand = (expandedKeysValue, info) => {
        var _a;
        setExpandedKeys(expandedKeysValue);
        setAutoExpandParent(false);
        return (_a = props.onExpand) === null || _a === void 0 ? void 0 : _a.call(props, expandedKeysValue, info);
    };
    const onClick = (event, node) => {
        var _a;
        if (node.children && !selectable) {
            treeRef.current.onNodeExpand(event, node);
        }
        (_a = props.onClick) === null || _a === void 0 ? void 0 : _a.call(props, event, node);
    };
    const onSearchInputChange = (event) => {
        const { value } = event.target;
        let found = false;
        const expandedKeys = dataList.map(item => {
            if (isSearchTitle(item.title) && item.title.indexOf(value) > -1) {
                found = true;
                return getParentKey(item.key, treeData);
            }
            return null;
        }).filter((item, i, self) => item && self.indexOf(item) === i);
        setExpandedKeys(expandedKeys);
        setSearchText(value);
        setAutoExpandParent(true);
        setSearchFound(found);
    };
    const onEditInputChange = useCallback((e, node) => {
        setTreeData(treeData => {
            const newTreeData = changeNode(node, { title: e.target.value }, treeData);
            onDataChange === null || onDataChange === void 0 ? void 0 : onDataChange(newTreeData, node, DataChangeType.EDITINPUTCHANGE);
            return newTreeData;
        });
    }, [onDataChange]);
    const onQuickEdit = (_, node) => {
        if (editable) {
            setTreeData(editNode(node, treeData));
        }
    };
    const onSave = useCallback((node) => {
        setTreeData(treeData => {
            const newTreeData = saveNode(node, treeData);
            onDataChange === null || onDataChange === void 0 ? void 0 : onDataChange(newTreeData, node, DataChangeType.SAVE);
            return newTreeData;
        });
    }, [onDataChange]);
    const onDelete = useCallback((node) => {
        setTreeData(treeData => {
            const newTreeData = deleteNode(node, treeData).filter(item => item !== null);
            onDataChange === null || onDataChange === void 0 ? void 0 : onDataChange(newTreeData, node, DataChangeType.DELETE);
            return newTreeData;
        });
    }, [onDataChange]);
    const onAdd = useCallback((node) => {
        setTreeData(treeData => {
            const newTreeData = addNode(node, treeData);
            onDataChange === null || onDataChange === void 0 ? void 0 : onDataChange(newTreeData, node, DataChangeType.ADD);
            return newTreeData;
        });
        setExpandedKeys(expandedKeys => [...new Set([...expandedKeys, node.key])]);
    }, [onDataChange]);
    const onDrop = (info) => {
        var _a, _b;
        const dropKey = info.node.key;
        const dragKey = info.dragNode.key;
        const dropPos = info.node.pos.split('-');
        const dropPosition = info.dropPosition - Number(dropPos[dropPos.length - 1]);
        const loop = (data, key, callback) => {
            for (let i = 0; i < data.length; i++) {
                if (data[i].key === key) {
                    return callback(data[i], i, data);
                }
                const children = data[i].children;
                if (children) {
                    loop(children, key, callback);
                }
            }
        };
        const data = [...treeData];
        // Find dragObject
        let dragObj = data[0];
        loop(data, dragKey, (item, index, arr) => {
            arr.splice(index, 1);
            dragObj = item;
        });
        if (dropPosition === 0) {
            // Drop on the content
            loop(data, dropKey, item => {
                item.children = item.children || [];
                item.children.unshift(dragObj);
            });
        }
        else {
            let ar = data;
            let i = 0;
            loop(data, dropKey, (_, index, arr) => {
                ar = arr;
                i = index;
            });
            if (dropPosition === -1) {
                ar.splice(i, 0, dragObj);
            }
            else {
                ar.splice(i + 1, 0, dragObj);
            }
        }
        setTreeData(data);
        (_a = props.onDataChange) === null || _a === void 0 ? void 0 : _a.call(props, data, info.node, DataChangeType.DROP);
        (_b = props.onDrop) === null || _b === void 0 ? void 0 : _b.call(props, info);
    };
    const handleClick = handleClickAndDbClick(onClick, onQuickEdit, 200);
    const { getPrefixCls } = React.useContext(ConfigContext);
    const prefixCls = getPrefixCls('tree', customizePrefixCls);
    const formatTreeData = useCallback((data) => {
        if (Array.isArray(data)) {
            const res = [];
            for (let i = 0; i < data.length; i++) {
                res[i] = formatTreeData(data[i]);
            }
            return res;
        }
        let newItem;
        let renderTitle;
        if (data.isEditable && isSearchTitle(data.title)) {
            renderTitle = (<div className={`${prefixCls}-edit-input`}>
                    <Input {...editInputProps} value={data.title} onChange={e => onEditInputChange(e, data)} onBlur={() => onSave(data)} size={'small'} autoFocus/>
                </div>);
        }
        else {
            let title = data.title;
            if (isSearchTitle(title) && searchText) {
                const index = title.indexOf(searchText);
                const beforeStr = title.substr(0, index);
                const afterStr = title.substr(index + searchText.length);
                title = index > -1 ? (<span>
                        {beforeStr}
                        <span className={`${prefixCls}-node-highlighttext`}>{searchText}</span>
                        {afterStr}
                    </span>) : (<span>{title}</span>);
            }
            const handleAdd = () => onAdd(data);
            const handleEdit = () => {
                setTreeData(treeData => editNode(data, treeData));
            };
            const handleDelete = () => onDelete(data);
            const onClickMap = {
                Add: handleAdd,
                Edit: handleEdit,
                Delete: handleDelete
            };
            const customPopoverUtilArray = Array.isArray(customPopoverUtil) ? customPopoverUtil : customPopoverUtil(data);
            const popoverContent = customPopoverUtilArray.map((item) => {
                const liProps = Object.assign(Object.assign({ key: item.key || item.text }, (!item.disabled && {
                    onClick: isUtilType(item.onClick) ? onClickMap[item.onClick]
                        : () => item.onClick(data, treeData)
                })), (item.disabled && {
                    className: `${prefixCls}-popover-util-li-disabled`,
                }));
                return (<li {...liProps}>
                        {item.text}
                    </li>);
            });
            const titleContent = <span className={`${prefixCls}-title-titleContent`}>{title}</span>;
            renderTitle = (<span className={`${prefixCls}-title-titleWithOps`}>
                    {titleEllipsis ? (<Popover zIndex={popoverZIndex === undefined ? 1051 : popoverZIndex} content={title}>
                                {titleContent}
                            </Popover>) : titleContent}
                    {showIconUtil && editable && (<span className={`${prefixCls}-icon-util`}>
                                <OutlinedPlus onClick={handleAdd}/>
                                <OutlinedEdit onClick={handleEdit}/>
                                <OutlinedDelete onClick={handleDelete}/>
                            </span>)}
                    {(showPopoverUtil && editable || customPopoverUtil !== DEFAULT_POPOVER_UTIL) && (<span className={`${prefixCls}-icon-util`}>
                                <PopoverComponent prefixCls={prefixCls} popoverZIndex={popoverZIndex} popoverContent={popoverContent}/>
                            </span>)}
                </span>);
        }
        newItem = update(data, { $merge: { title: renderTitle } });
        if (data.children) {
            newItem = update(newItem, { $merge: { children: formatTreeData(data.children) } });
        }
        return newItem;
    }, [editable, onDelete, onSave, onAdd, onEditInputChange, showIconUtil, showPopoverUtil, searchText, prefixCls, customPopoverUtil, titleEllipsis, popoverZIndex]);
    const formatedTreeData = useMemo(() => formatTreeData(treeData), [treeData, formatTreeData]);
    const classes = classNames(prefixCls, className, titleEllipsis ? `${prefixCls}-with-title-ellipsis` : '');
    // 兼容不传treeData数据直接传treeNode子组件的场景
    const newProps = Object.assign({}, props);
    if (treeDataOrigin) {
        newProps.treeData = formatedTreeData;
    }
    // expandedKeys和defaultExpandAll冲突需动态赋值
    if (props && props.defaultExpandAll) {
        if (expandedKeys.length) {
            newProps.expandedKeys = expandedKeys;
        }
    }
    else {
        newProps.expandedKeys = expandedKeys;
    }
    useEffect(() => {
        if (Array.isArray(treeDataOrigin)) {
            setTreeData(treeDataOrigin);
        }
    }, [treeDataOrigin]);
    useEffect(() => {
        Array.isArray(expandedKeysInProps) && setExpandedKeys(expandedKeysInProps);
    }, [expandedKeysInProps]);
    return (<>
        {searchable && <Input placeholder={searchPlaceholder} className={`${prefixCls}-search-input`} onChange={onSearchInputChange}/>}
        {searchText && !searchFound ? (`未找到与${searchText}相关的结果`) : ((!treeDataOrigin || !!formatedTreeData.length) && <RcTree ref={treeRef} {...newProps} className={classes} prefixCls={prefixCls} onClick={handleClick} onExpand={onExpand} onDrop={onDrop} checkable={checkable ? <span className={`${prefixCls}-checkbox-inner`}/> : checkable} autoExpandParent={autoExpandParent} switcherIcon={(nodeProps) => renderSwitcherIcon(prefixCls, switcherIcon, showLine, nodeProps)} dropIndicatorRender={dropIndicatorRender} draggable={draggableConfig}/>)}
    </>);
});
Tree.TreeNode = TreeNode;
export default Tree;
